import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Row, Col, Input, Select, Button, Tag, Space, message, Modal, List, Typography } from 'antd';
import { SearchOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import { fetchChannels, type ChannelDto } from '../services/channelService';

interface VariantValue {
  variantName: string;
  variantValue: string;
}

interface StockItem {
  id: number;
  productId: number;
  productCode: string;
  productName: string;
  productBrandName?: string | null;
  isPriceVariant: boolean;
  productVariantId?: number;
  variantSku?: string;
  variantValues: VariantValue[] | null;
  warehouseId: number;
  warehouseName: string;
  quantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  shelfCode?: string; // Raf kodu (virgülle ayrılmış)
  shelfLocations?: Array<{ shelfCode: string; quantity: number }>; // Popup için detaylı raf bilgileri
  purchasePrice?: number;
  retailPrice?: number;
  wholesalePrice?: number;
  taxRate?: number;
  stockStatus: string;
  lastUpdated: string;
}

interface Warehouse {
  id: number;
  warehouseName: string;
}

interface Category {
  id: number;
  categoryName: string;
  parentId?: number | null;
}

interface StockSummary {
  totalStockQuantity: number;
  totalReservedQuantity: number;
  totalAvailableQuantity: number;
  lowStockCount: number;
}

interface PaginationResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  summary?: StockSummary;
}

const Stock: React.FC = () => {
  const { user } = useAuth();
  const canSeePurchasePrice = user?.roleCodes?.includes('ADMIN') ?? false;
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [channels, setChannels] = useState<ChannelDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState<number | undefined>();
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>();
  const [selectedChannel, setSelectedChannel] = useState<number | undefined>();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [summary, setSummary] = useState<StockSummary>({
    totalStockQuantity: 0,
    totalReservedQuantity: 0,
    totalAvailableQuantity: 0,
    lowStockCount: 0,
  });
  const { baseCurrencySymbol } = useCurrency();
  
  // Raf lokasyonları popup state
  const [shelfModalVisible, setShelfModalVisible] = useState(false);
  const [selectedShelfLocations, setSelectedShelfLocations] = useState<Array<{ shelfCode: string; quantity: number }>>([]);

  const loadWarehouses = async () => {
    try {
      const { data } = await api.get<Warehouse[]>('/warehouses');
      setWarehouses(data);
    } catch (error) {
      console.error('Depo verileri yüklenirken hata oluştu:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const { data } = await api.get<Category[]>('/categories');
      setCategories(data);
    } catch (error) {
      console.error('Kategoriler yüklenirken hata oluştu:', error);
    }
  };

  const loadChannels = async () => {
    try {
      const data = await fetchChannels();
      setChannels(data.filter(c => c.isActive)); // Sadece aktif kanalları göster
    } catch (error) {
      console.error('Kanallar yüklenirken hata oluştu:', error);
    }
  };

  const loadStockItems = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.current,
        pageSize: pagination.pageSize,
      };
      
      if (selectedWarehouse) {
        params.warehouseId = selectedWarehouse;
      }
      
      if (selectedCategory) {
        params.categoryId = selectedCategory;
      }
      
      if (selectedChannel) {
        params.channelId = selectedChannel;
      }
      
      if (searchText.trim()) {
        params.search = searchText.trim();
      }

      const { data } = await api.get<PaginationResponse<StockItem>>('/stock/all', { params });
      
      // Debug: İlk birkaç kaydı kontrol et
      if (process.env.NODE_ENV === 'development' && data.data.length > 0) {
        console.log('Stock API Response - First item:', data.data[0]);
        console.log('First item shelfCode:', data.data[0].shelfCode);
      }
      
      setStockItems(data.data);
      setPagination(prev => ({
        ...prev,
        total: data.totalCount,
      }));
      
      // Update summary from backend response
      if (data.summary) {
        setSummary(data.summary);
      }
    } catch (error: any) {
      console.error('Stok verileri yüklenirken hata oluştu:', error);
      message.error('Stok verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouse, selectedCategory, selectedChannel, searchText, pagination.current, pagination.pageSize]);

  useEffect(() => {
    loadWarehouses();
    loadCategories();
    loadChannels();
  }, []);

  useEffect(() => {
    loadStockItems();
  }, [loadStockItems]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CRITICAL': return 'red';
      case 'LOW': return 'orange';
      case 'HIGH': return 'blue';
      case 'NORMAL': return 'green';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'CRITICAL': return 'Kritik';
      case 'LOW': return 'Düşük';
      case 'HIGH': return 'Yüksek';
      case 'NORMAL': return 'Normal';
      default: return status;
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    loadStockItems();
  };

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  // Kategorileri hiyerarşik sırala (parent-child ilişkisine göre)
  const getSortedCategoriesHierarchically = (categories: Category[]): Array<Category & { level: number; fullName: string }> => {
    const result: Array<Category & { level: number; fullName: string }> = [];
    
    // Recursive olarak kategori ve alt kategorilerini ekle
    const addCategoryAndChildren = (parentId: number | null | undefined, level: number = 0) => {
      const children = categories
        .filter(c => c.parentId === parentId)
        .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
      
      children.forEach(cat => {
        // Kategori yolunu oluştur (parent > child formatında)
        let fullName = cat.categoryName;
        let currentCat = cat;
        while (currentCat.parentId) {
          const parent = categories.find(c => c.id === currentCat.parentId);
          if (!parent) break;
          fullName = `${parent.categoryName} > ${fullName}`;
          currentCat = parent;
        }
        
        result.push({
          ...cat,
          level,
          fullName
        });
        
        // Alt kategorileri recursive ekle
        addCategoryAndChildren(cat.id, level + 1);
      });
    };
    
    // Kök kategorilerden başla (parentId null/undefined olanlar)
    addCategoryAndChildren(null, 0);
    addCategoryAndChildren(undefined, 0);
    
    return result;
  };

  const categoriesWithHierarchy = getSortedCategoriesHierarchically(categories);

  const columns = [
    {
      title: 'Ürün Kodu / SKU',
      key: 'code',
      width: 150,
      render: (_: any, record: StockItem) => {
        // Varyant varsa varyant SKU'sunu göster, yoksa ürün kodunu göster
        return record.variantSku || record.productCode;
      },
    },
    {
      title: 'Marka',
      dataIndex: 'productBrandName',
      key: 'productBrandName',
      width: 120,
      render: (name: string | null | undefined) => name ?? '-',
    },
    {
      title: 'Ürün Adı',
      dataIndex: 'productName',
      key: 'productName',
      width: 200,
    },
    {
      title: 'Depo',
      dataIndex: 'warehouseName',
      key: 'warehouseName',
      width: 120,
    },
    {
      title: 'Raf Kodu',
      dataIndex: 'shelfCode',
      key: 'shelfCode',
      width: 200,
      render: (text: any, record: StockItem) => {
        const shelfCode = record.shelfCode || text;
        const shelfLocations = record.shelfLocations || [];
        
        if (shelfCode && shelfLocations.length > 0) {
          return (
            <Space>
              <span style={{ 
                fontWeight: 'bold', 
                color: '#1890ff',
                backgroundColor: '#e6f7ff',
                padding: '2px 8px',
                borderRadius: '4px',
                display: 'inline-block'
              }}>
                {shelfCode}
              </span>
              <InfoCircleOutlined 
                style={{ 
                  color: '#1890ff', 
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
                onClick={() => {
                  setSelectedShelfLocations(shelfLocations);
                  setShelfModalVisible(true);
                }}
              />
            </Space>
          );
        }
        return <span style={{ color: '#999' }}>-</span>;
      },
    },
    {
      title: 'Toplam Stok',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (quantity: number) => <strong>{quantity}</strong>,
    },
    ...(canSeePurchasePrice
      ? [{
          title: 'Alış Fiyatı (KDV dahil)',
          dataIndex: 'purchasePrice',
          key: 'purchasePrice',
          width: 120,
          render: (price: number, record: StockItem) =>
            price != null ? `${baseCurrencySymbol}${(price * (1 + (record.taxRate ?? 20) / 100)).toFixed(2)}` : '-',
        }]
      : []),
    {
      title: 'Perakende Fiyatı (KDV dahil)',
      dataIndex: 'retailPrice',
      key: 'retailPrice',
      width: 140,
      render: (price: number, record: StockItem) =>
        price != null ? `${baseCurrencySymbol}${(price * (1 + (record.taxRate ?? 20) / 100)).toFixed(2)}` : '-',
    },
    {
      title: 'Toptan Fiyatı (KDV dahil)',
      key: 'wholesalePrice',
      dataIndex: 'wholesalePrice',
      width: 120,
      render: (price: number, record: StockItem) =>
        price != null ? `${baseCurrencySymbol}${(price * (1 + (record.taxRate ?? 20) / 100)).toFixed(2)}` : '-',
    },
    {
      title: 'Durum',
      dataIndex: 'stockStatus',
      key: 'stockStatus',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Son Güncelleme',
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      width: 150,
      render: (date: string) => new Date(date).toLocaleString('tr-TR'),
    },
  ];

  // Note: Summary statistics would need a separate API endpoint for accurate totals across all pages
  // Use summary from backend instead of calculating from current page items
  const totalStock = summary.totalStockQuantity;
  const totalReserved = summary.totalReservedQuantity;
  const totalAvailable = summary.totalAvailableQuantity;
  const lowStockCount = summary.lowStockCount;

  return (
    <div>
      <h1>Stok Yönetimi</h1>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                {totalStock}
              </div>
              <div>Toplam Stok</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                {totalAvailable}
              </div>
              <div>Kullanılabilir</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fa8c16' }}>
                {totalReserved}
              </div>
              <div>Rezerve</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f5222d' }}>
                {lowStockCount}
              </div>
              <div>Düşük Stok</div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="Ürün ara..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 250 }}
          />
          <Select
            placeholder="Kategori seçiniz"
            value={selectedCategory}
            onChange={(value) => {
              setSelectedCategory(value);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ width: 300 }}
            allowClear
            showSearch
            filterOption={(input, option) => {
              const categoryName = categoriesWithHierarchy.find(c => c.id === option?.value)?.categoryName || '';
              return categoryName.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {categoriesWithHierarchy.map(category => (
              <Select.Option key={category.id} value={category.id}>
                <span style={{ paddingLeft: `${category.level * 20}px` }}>
                  {category.level > 0 && '└─ '}
                  {category.categoryName}
                </span>
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder="Depo seçiniz"
            value={selectedWarehouse}
            onChange={(value) => {
              setSelectedWarehouse(value);
              setSelectedChannel(undefined); // Kanal seçildiğinde depo filtresini temizle
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ width: 200 }}
            allowClear
            showSearch
            filterOption={(input, option) => {
              const warehouseName = warehouses.find(w => w.id === option?.value)?.warehouseName || '';
              return warehouseName.toLowerCase().includes(input.toLowerCase());
            }}
            disabled={!!selectedChannel} // Kanal seçildiğinde depo seçimi devre dışı
          >
            {warehouses.map(warehouse => (
              <Select.Option key={warehouse.id} value={warehouse.id}>
                {warehouse.warehouseName}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder="Kanal seçiniz"
            value={selectedChannel}
            onChange={(value) => {
              setSelectedChannel(value);
              setSelectedWarehouse(undefined); // Kanal seçildiğinde depo filtresini temizle
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ width: 200 }}
            allowClear
            showSearch
            filterOption={(input, option) => {
              const channelName = channels.find(c => c.id === option?.value)?.name || '';
              return channelName.toLowerCase().includes(input.toLowerCase());
            }}
            disabled={!!selectedWarehouse} // Depo seçildiğinde kanal seçimi devre dışı
          >
            {channels.map(channel => (
              <Select.Option key={channel.id} value={channel.id}>
                {channel.name}
              </Select.Option>
            ))}
          </Select>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            Ara
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadStockItems}>
            Yenile
          </Button>
        </Space>
      </Card>

      {/* Stock Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={stockItems}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Toplam ${total} kayıt`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Raf Lokasyonları Popup */}
      <Modal
        title="Raf Lokasyonları"
        open={shelfModalVisible}
        onCancel={() => setShelfModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setShelfModalVisible(false)}>
            Kapat
          </Button>
        ]}
        width={500}
      >
        <List
          dataSource={selectedShelfLocations}
          renderItem={(item) => (
            <List.Item>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Typography.Text strong>{item.shelfCode}</Typography.Text>
                <Typography.Text type="secondary">
                  Miktar: {item.quantity.toFixed(2)}
                </Typography.Text>
              </Space>
            </List.Item>
          )}
          locale={{ emptyText: 'Raf lokasyonu bulunamadı' }}
        />
      </Modal>
    </div>
  );
};

export default Stock;
